import { Purchases } from "@revenuecat/purchases-js";

type RevenueCatEnvironment = {
  apiKey?: string;
  environment?: string;
};

let pendingUserSynchronization: Promise<Purchases> | null = null;

export function getRevenueCatPublicApiKey(
  overrides: RevenueCatEnvironment = {}
): string {
  const apiKey = overrides.apiKey !== undefined
    ? overrides.apiKey
    : process.env.REVENUECAT_WEB_PUBLIC_API_KEY;
  const environment = overrides.environment ?? process.env.NODE_ENV;

  if (!apiKey) {
    throw new Error(
      "REVENUECAT_WEB_PUBLIC_API_KEY est manquante. Utilisez uniquement la clé publique du SDK RevenueCat."
    );
  }
  if (!apiKey.startsWith("strp_")) {
    throw new Error(
      "REVENUECAT_WEB_PUBLIC_API_KEY doit être une clé publique RevenueCat Stripe Billing (préfixe strp_)."
    );
  }
  if (environment === "production" && apiKey.startsWith("strp_sb_")) {
    throw new Error(
      "La clé RevenueCat sandbox ne peut pas être utilisée dans un build de production."
    );
  }

  return apiKey;
}

async function synchronizePurchasesUser(uid: string): Promise<Purchases> {
  if (!uid) {
    throw new Error("RevenueCat ne peut pas être initialisé sans identifiant utilisateur.");
  }

  const purchases = Purchases.isConfigured()
    ? Purchases.getSharedInstance()
    : Purchases.configure({
        apiKey: getRevenueCatPublicApiKey(),
        appUserId: uid,
      });

  if (purchases.getAppUserId() !== uid) {
    await purchases.changeUser(uid);
  }

  return purchases;
}

export function ensurePurchasesUser(uid: string): Promise<Purchases> {
  const previousSynchronization = pendingUserSynchronization;
  const synchronization = (previousSynchronization ?? Promise.resolve(null))
    .catch(() => null)
    .then(() => synchronizePurchasesUser(uid));

  pendingUserSynchronization = synchronization;
  const releaseSynchronization = () => {
    if (pendingUserSynchronization === synchronization) {
      pendingUserSynchronization = null;
    }
  };
  void synchronization.then(releaseSynchronization, releaseSynchronization);

  return synchronization;
}
